export type RequestStatus = 'recue' | 'in-progress' | 'resolue'

export interface TimelineStep {
  date: string
  label: string
}

export interface RequestAttachment {
  name: string
  /** Drives the icon shown next to the file. */
  type: 'image' | 'pdf' | 'form'
  size: string
}

export type MessageAuthor = 'citoyen' | 'ville'

export interface RequestMessage {
  id: string
  author: MessageAuthor
  authorName: string
  sentAt: string
  body: string
  attachment?: RequestAttachment
  /** Set when the Town expects something back from the citizen. */
  awaiting?: 'document' | 'info'
}

export interface CitizenRequest {
  id: string
  reference: string
  title: string
  /** Departement responsable (ex. Public Works). */
  service: string
  /** Sous-categorie de la requete (ex. Garbage collection). */
  category: string
  address: string
  fullAddress: string
  submittedOn: string
  createdOn: string
  resolvedOn: string | null
  createdBy: string
  status: RequestStatus
  description: string
  attachments: RequestAttachment[]
  timeline: TimelineStep[]
  messages: RequestMessage[]
  response?: string
}

export const statusLabels: Record<RequestStatus, string> = {
  recue: 'Received',
  'in-progress': 'In progress',
  resolue: 'Resolved',
}

export const statusOrder: RequestStatus[] = ['recue', 'in-progress', 'resolue']

export const quickReports = ['Road / pothole', 'Street lighting', 'Tree', 'Water', 'Collections', 'Other']

export const profileAddress = '5108 - 46 Street'

/** Very small keyword classifier used to simulate ATNA's understanding. */
export const intents = [
  {
    keywords: ['pothole', 'pot hole', 'hole', 'asphalt', 'road', 'pavement'],
    label: 'Pothole',
    service: 'Roads',
    question: 'How long have you noticed the problem?',
    answers: ['Today', 'This week', 'More than a month'],
    photoHint: true,
  },
  {
    keywords: ['street light', 'streetlight', 'light', 'lighting', 'bulb', 'lamp'],
    label: 'Street light out',
    service: 'Street lighting',
    question: 'Is the street light completely out, or flickering?',
    answers: ['Completely out', 'It flickers', 'It stays on during the day'],
    photoHint: false,
  },
  {
    keywords: ['tree', 'branch', 'limb', 'fallen'],
    label: 'Tree or branch to clear',
    service: 'Parks and open spaces',
    question: 'Is the tree blocking traffic?',
    answers: ['Yes, the road is blocked', 'No, it is on the sidewalk', 'No, it is on private property'],
    photoHint: true,
  },
  {
    keywords: ['water', 'leak', 'leaking', 'hydrant', 'wet sidewalk'],
    label: 'Water leak',
    service: 'Water utility',
    question: 'Is the water running continuously?',
    answers: ['Yes, continuously', 'On and off', 'I am not sure'],
    photoHint: true,
  },
  {
    keywords: ['cart', 'bin', 'garbage', 'recycling', 'collection', 'collected'],
    label: 'Cart not collected',
    service: 'Collections',
    question: 'Which cart was missed?',
    answers: ['Recycling', 'Garbage', 'Yard waste'],
    photoHint: false,
  },
]

export const emergencyKeywords = [
  'power line',
  'live wire',
  'electrocut',
  'fire',
  'gas leak',
  'gas',
  'injured',
  'accident',
  'smoke',
]

export const requests: CitizenRequest[] = [
  {
    id: 'req-2903',
    reference: '#REQ-2903',
    title: 'Animal found',
    service: 'Animal control',
    category: 'Stray or found animal',
    address: 'Lakeshore Drive Park, Bonnyville',
    fullAddress: '4801 - 50 Avenue, Bonnyville, Alberta, T9N 2H8',
    submittedOn: 'Sent today',
    createdOn: '09/10/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'recue',
    description:
      'Dog found near the playground with no readable licence tag. The dog is calm and being held on a leash until someone can take it in.',
    attachments: [{ name: 'dog-found-park.jpg', type: 'image', size: '2.1 MB' }],
    timeline: [{ date: 'Today', label: 'Report received' }],
    messages: [
      {
        id: 'msg-2903-1',
        author: 'ville',
        authorName: 'Animal control',
        sentAt: 'Today, 9:42 a.m.',
        body: 'Thank you for the report. An animal control officer has been assigned and will contact you if the animal is still there.',
      },
    ],
  },
  {
    id: 'req-2841',
    reference: '#REQ-2841',
    title: 'Pothole',
    service: 'Public Works',
    category: 'Roads and pavement',
    address: '5108 - 46 Street',
    fullAddress: '5108 - 46 Street, Bonnyville, Alberta, T9N 2G1',
    submittedOn: 'Sent August 26',
    createdOn: '08/26/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'in-progress',
    description:
      'Large pothole in front of the property, in the right-hand lane. It has widened since the last rainfall and several vehicles swerve into the oncoming lane to avoid it.',
    attachments: [
      { name: 'pothole-46-street.jpg', type: 'image', size: '1.8 MB' },
      { name: 'street-view.jpg', type: 'image', size: '2.4 MB' },
    ],
    timeline: [
      { date: 'Aug. 26', label: 'Request received' },
      { date: 'Aug. 27', label: 'Forwarded to Public Works' },
      { date: 'Aug. 28', label: 'In progress' },
      { date: 'Aug. 31', label: 'Inspection scheduled' },
    ],
    messages: [
      {
        id: 'msg-2841-1',
        author: 'ville',
        authorName: 'Public Works · Karen Whitford',
        sentAt: 'Aug. 27, 10:15 a.m.',
        body: 'Hello, your report has been forwarded to the roads crew. An inspection is planned for this week.',
      },
      {
        id: 'msg-2841-2',
        author: 'citoyen',
        authorName: 'You',
        sentAt: 'Aug. 28, 8:03 a.m.',
        body: 'Thanks. The hole has grown again since Sunday’s rain — it is now roughly 60 cm across.',
      },
      {
        id: 'msg-2841-3',
        author: 'ville',
        authorName: 'Public Works · Karen Whitford',
        sentAt: 'Aug. 29, 2:30 p.m.',
        body: 'Thank you for the detail. Could you confirm whether the pothole is in front of your driveway or closer to the intersection? A photo taken from further back would help us pinpoint it.',
        awaiting: 'info',
      },
      {
        id: 'msg-2841-4',
        author: 'ville',
        authorName: 'Public Works · Karen Whitford',
        sentAt: 'Aug. 31, 9:05 a.m.',
        body: 'A vehicle damage claim can be opened if applicable. Please complete the attached form and return it to us within 15 days.',
        attachment: { name: 'vehicle-damage-claim-form.pdf', type: 'form', size: '184 KB' },
        awaiting: 'document',
      },
    ],
  },
  {
    id: 'req-2790',
    reference: '#REQ-2790',
    title: 'Street light out',
    service: 'Public Works',
    category: 'Street lighting',
    address: '5116 - 46 Street',
    fullAddress: '5116 - 46 Street, Bonnyville, Alberta, T9N 2G1',
    submittedOn: 'Sent August 12',
    createdOn: '08/12/26',
    resolvedOn: '08/18/26',
    createdBy: 'ATNA',
    status: 'resolue',
    description:
      'The street light in front of 5116 stayed out at night, leaving the sidewalk dark between the two intersections.',
    attachments: [],
    timeline: [
      { date: 'Aug. 12', label: 'Request received' },
      { date: 'Aug. 13', label: 'Forwarded to the street lighting crew' },
      { date: 'Aug. 18', label: 'Resolved' },
    ],
    messages: [
      {
        id: 'msg-2790-1',
        author: 'ville',
        authorName: 'Street lighting',
        sentAt: 'Aug. 18, 4:20 p.m.',
        body: 'The bulb was replaced today by the maintenance crew. The request is now closed — write to us again if the problem comes back.',
      },
    ],
    response: 'The bulb was replaced on August 18 by the maintenance crew.',
  },
]

export type PublicReportCategory = 'danger' | 'voirie' | 'eclairage' | 'arbre'

export type PublicReportStatus = 'nouveau' | 'in-progress' | 'resolue'

export interface PublicReport {
  id: string
  category: PublicReportCategory
  title: string
  address: string
  distance: string
  reportedAgo: string
  status: PublicReportStatus
  confirmations: number
  lat: number
  lng: number
}

export const reportCategories: Record<PublicReportCategory, { label: string; color: string }> = {
  danger: { label: 'Danger', color: '#d93b30' },
  voirie: { label: 'Roads', color: '#d97706' },
  eclairage: { label: 'Street lighting', color: '#8a6212' },
  arbre: { label: 'Trees', color: '#3f6212' },
}

export const publicReportStatusLabels: Record<PublicReportStatus, string> = {
  nouveau: 'New',
  'in-progress': 'Crew on the way',
  resolue: 'Fixed',
}

/** Position approximative du domicile au profil (Zone 2, Bonnyville). */
export const profileCoordinates: [number, number] = [54.2686, -110.7369]

export const publicReports: PublicReport[] = [
  {
    id: 'pub-1',
    category: 'voirie',
    title: 'Deep pothole',
    address: '50 Avenue (Main Street), near 48 Street',
    distance: '300 m',
    reportedAgo: '25 min ago',
    status: 'nouveau',
    confirmations: 7,
    lat: 54.2708,
    lng: -110.73294,
  },
  {
    id: 'pub-2',
    category: 'eclairage',
    title: 'Street light out',
    address: '5116 - 46 Street',
    distance: '450 m',
    reportedAgo: '2 h ago',
    status: 'in-progress',
    confirmations: 12,
    lat: 54.2662,
    lng: -110.74146,
  },
  {
    id: 'pub-3',
    category: 'arbre',
    title: 'Branch down on the sidewalk',
    address: 'Lakeshore Drive Park',
    distance: '650 m',
    reportedAgo: '40 min ago',
    status: 'nouveau',
    confirmations: 4,
    lat: 54.273,
    lng: -110.74243,
  },
  {
    id: 'pub-4',
    category: 'voirie',
    title: 'Sunken pavement',
    address: '47 Avenue at Lakeshore Drive',
    distance: '1.1 km',
    reportedAgo: '5 h ago',
    status: 'in-progress',
    confirmations: 18,
    lat: 54.2621,
    lng: -110.72801,
  },
  {
    id: 'pub-5',
    category: 'eclairage',
    title: 'Flickering street light',
    address: '43 Street',
    distance: '800 m',
    reportedAgo: '1 h ago',
    status: 'nouveau',
    confirmations: 9,
    lat: 54.2647,
    lng: -110.74687,
  },
  {
    id: 'pub-6',
    category: 'danger',
    title: 'Traffic light out of service',
    address: '49 Avenue at 45 Street',
    distance: '1.4 km',
    reportedAgo: '15 min ago',
    status: 'in-progress',
    confirmations: 31,
    lat: 54.2752,
    lng: -110.72405,
  },
  {
    id: 'pub-7',
    category: 'voirie',
    title: 'Faded road markings',
    address: 'Lakeshore Drive',
    distance: '1.6 km',
    reportedAgo: 'yesterday',
    status: 'resolue',
    confirmations: 3,
    lat: 54.2594,
    lng: -110.74375,
  },
  {
    id: 'pub-8',
    category: 'arbre',
    title: 'Tree to prune near power lines',
    address: '52 Avenue',
    distance: '2.0 km',
    reportedAgo: '3 h ago',
    status: 'nouveau',
    confirmations: 6,
    lat: 54.2781,
    lng: -110.7381,
  },
]

export const detailQuestions = [
  'Where does my request stand?',
  'When will it be handled?',
  'Is there anything else I need to do?',
]
